import { Zap, Gauge, AlarmClock, Info, Flag } from "lucide-react";

const formatNumber = (n) =>
  typeof n === "number" ? (Number.isInteger(n) ? n.toString() : n.toFixed(1)) : null;

const compare = (country, usa, lowerIsBetter = true) => {
  if (typeof country !== "number" || typeof usa !== "number" || usa === 0) return null;
  const ratio = country / usa;
  if (lowerIsBetter) {
    if (ratio < 0.7) return { tone: "positive", label: `${(usa / country).toFixed(1)}× better than US` };
    if (ratio > 1.3) return { tone: "negative", label: `${ratio.toFixed(1)}× more than US` };
    return { tone: "neutral", label: "Similar to US" };
  }
  if (ratio > 1.3) return { tone: "positive", label: `${ratio.toFixed(1)}× better than US` };
  if (ratio < 0.7) return { tone: "negative", label: `${(usa / country).toFixed(1)}× worse than US` };
  return { tone: "neutral", label: "Similar to US" };
};

/**
 * Energy Grid Dependability card — renders SAIDI/SAIFI/CAIDI/LOLE side by side with USA baseline.
 */
export const EnergyGridCard = ({ energy }) => {
  if (!energy || typeof energy !== "object") return null;

  const metrics = [
    {
      key: "saidi",
      icon: AlarmClock,
      label: "SAIDI",
      sub: "Outage minutes / customer / year",
      country: energy.saidi_minutes_per_year,
      usa: energy.saidi_minutes_per_year_usa,
      suffix: " min",
      lowerIsBetter: true,
    },
    {
      key: "saifi",
      icon: Gauge,
      label: "SAIFI",
      sub: "Interruptions / customer / year",
      country: energy.saifi_interruptions_per_year,
      usa: energy.saifi_interruptions_per_year_usa,
      suffix: "",
      lowerIsBetter: true,
    },
    {
      key: "caidi",
      icon: AlarmClock,
      label: "CAIDI",
      sub: "Duration per interruption",
      country: energy.caidi_minutes_per_interruption,
      usa: energy.caidi_minutes_per_interruption_usa,
      suffix: " min",
      lowerIsBetter: true,
    },
    {
      key: "lole",
      icon: AlarmClock,
      label: "LOLE",
      sub: "Loss-of-load expectation / year",
      country: energy.lole_hours_per_year,
      usa: energy.lole_hours_per_year_usa,
      suffix: " h",
      lowerIsBetter: true,
    },
  ].filter((m) => formatNumber(m.country));

  if (metrics.length === 0 && !energy.notes) return null;

  return (
    <div
      className="bg-card rounded-2xl border border-primary/10 p-6 md:p-8 mb-6"
      data-testid="energy-grid-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <Zap className="h-5 w-5 text-accent" />
        <h3 className="text-xl font-bold text-primary">Energy Grid Dependability</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        How reliable daily power is — compared to the USA baseline.
      </p>

      {metrics.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-3 mb-4">
          {metrics.map(({ key, icon: Icon, label, sub, country, usa, suffix, lowerIsBetter }) => {
            const cmp = compare(country, usa, lowerIsBetter);
            return (
              <div
                key={key}
                className="rounded-xl border border-primary/10 bg-primary/5 p-4"
                data-testid={`energy-${key}`}
              >
                <div className="flex items-center gap-1 mb-2">
                  <Icon className="h-3 w-3 text-muted-foreground" />
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    {label}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {/* Country side */}
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-primary/60 mb-0.5">
                      Country
                    </p>
                    <p className="text-2xl font-bold text-primary leading-tight">
                      {formatNumber(country)}
                      <span className="text-sm font-normal text-muted-foreground">
                        {suffix}
                      </span>
                    </p>
                  </div>

                  {/* USA side */}
                  <div className="border-l border-primary/10 pl-3">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Flag className="h-2.5 w-2.5 text-muted-foreground" />
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        USA
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-muted-foreground leading-tight">
                      {formatNumber(usa) ?? "—"}
                      {formatNumber(usa) && (
                        <span className="text-sm font-normal text-muted-foreground">
                          {suffix}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mt-2">{sub}</p>

                {cmp && (
                  <p
                    className={`text-xs font-medium mt-1 ${
                      cmp.tone === "positive"
                        ? "text-accent"
                        : cmp.tone === "negative"
                        ? "text-destructive"
                        : "text-muted-foreground"
                    }`}
                    data-testid={`energy-${key}-compare`}
                  >
                    {cmp.label}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {energy.notes && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-accent/5 border border-accent/20 mb-3">
          <Info className="h-4 w-4 text-accent mt-0.5 shrink-0" />
          <p className="text-sm text-foreground/80 leading-relaxed">{energy.notes}</p>
        </div>
      )}

      {(energy.source || energy.source_usa) && (
        <div className="text-xs text-muted-foreground italic space-y-0.5" data-testid="energy-source">
          {energy.source && <p>Country source: {energy.source}</p>}
          {energy.source_usa && <p>USA source: {energy.source_usa}</p>}
        </div>
      )}
    </div>
  );
};

export default EnergyGridCard;
